import { useEffect, useRef, useState } from "react";
import { FileText, X } from "lucide-react";
import api from "../../api";
import Portal from "../common/Portal";

// Note template picker + sections (backend clinical/notes/note_templates.py).
// "default" keeps the page's own Clinical note layout; any other template
// replaces it with that template's sections, saved as template_values.
// The format can change any time before the note is signed. Switching away
// from sections that already have text asks first: those sections are hidden
// (and not saved) under the new format.
export function NoteTemplatePicker({ templates, code, onChange, disabled = false, hasContent = false }) {
  if (!templates || templates.length <= 1) return null;
  const change = (next) => {
    if (hasContent && !window.confirm("Switch the note format? Text in the current sections won't be saved under the new format.")) return;
    onChange(next);
  };
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-xl border border-secondary-200 bg-white px-4 py-2.5">
      <FileText className="w-4 h-4 text-primary-600" />
      <label htmlFor="note_template" className="text-xs font-semibold text-secondary-700">Note format</label>
      <select id="note_template" value={code || "default"} disabled={disabled} onChange={(e) => change(e.target.value)}
        className="rounded-md border border-secondary-300 px-2 py-1 text-sm bg-white">
        {templates.map((t) => <option key={t.code} value={t.code}>{t.name}{t.source === "org" ? " (your clinic)" : ""}</option>)}
      </select>
    </div>
  );
}

export default function NoteTemplateSections({ template, values, onChange, disabled = false }) {
  if (!template || !template.sections) return null;
  const v = values || {};
  return (
    <div className="p-4 space-y-4 bg-sky-50/20 border border-sky-200/60 rounded-xl">
      <h4 className="text-[11px] font-bold text-gray-500 uppercase tracking-wide">{template.name}</h4>
      {template.sections.map((s) => (
        <div key={s.key}>
          <label htmlFor={`tpl_${s.key}`} className="text-gray-700 font-semibold text-xs mb-1 block">{s.label}</label>
          {s.hint && <p className="text-[11px] text-gray-500 mb-1.5">{s.hint}</p>}
          <textarea id={`tpl_${s.key}`} value={v[s.key] || ""} disabled={disabled}
            onChange={(e) => onChange({ ...v, [s.key]: e.target.value })}
            className="w-full rounded-md border border-gray-300 p-2 text-sm bg-white min-h-[140px]" />
        </div>
      ))}
    </div>
  );
}

// Which format this session uses (backend note_templates.session_format): the
// picked one, else the clinic default. Starts an empty note in it, so the
// note and the session assistant's drafts follow the same layout.
const DEFAULT_NOTE_FIELDS = ["summary", "mood", "mainthemes", "resistanceorengagement", "interventions",
  "interventionseffectiveness", "updatedhypothesis", "sessionoutcome", "nextplan"];
const noteHasText = (d) => DEFAULT_NOTE_FIELDS.some((k) => (d?.[k] || "").trim())
  || Object.values(d?.template_values || {}).some((v) => (v || "").trim());

// Starts an empty note in the session's format: the one picked for this
// session, else the clinic default (Settings -> Clinical & Safety).
export function useSessionNoteFormat(appointmentId, ready, data, setData) {
  const dataRef = useRef(data);
  dataRef.current = data;
  useEffect(() => {
    if (!appointmentId || !ready) return;
    api.getNoteFormat(appointmentId).then((r) => {
      const { code, source } = r.data || {};
      const prev = dataRef.current || {};
      if ((prev.template_code && prev.template_code !== "default") || noteHasText(prev)) return;
      if (!code || code === "default" || (source !== "session" && source !== "clinic")) return;
      setData((p) => ({ ...p, template_code: code, template_values: p.template_values || {} }));
    }).catch(() => {});
  }, [appointmentId, ready]); // eslint-disable-line
}

// Checked when the clinician presses Complete, like the risk assessment:
// true when no format was picked for this session and the clinic has no
// default, so the page asks before completing.
export async function noteFormatNeeded(appointmentId, templateCode) {
  if (!appointmentId || (templateCode && templateCode !== "default")) return false;
  try {
    const r = await api.getNoteFormat(appointmentId);
    return Boolean(r.data?.needs_choice);
  } catch {
    return false;
  }
}

// Asked at Complete when no format was picked and the clinic has no default.
// current: the format the note is written in now (preselected, listed first).
// Confirming it completes; choosing another switches the note to that format.
const DEFAULT_SECTIONS = ["Mood", "Themes", "Interventions", "Outcome", "Next plan"];

export function NoteFormatPrompt({ open, templates, current = "default", onPick, onLater }) {
  const [selected, setSelected] = useState(current);
  useEffect(() => { if (open) setSelected(current); }, [open, current]);
  if (!open || !templates || templates.length <= 1) return null;
  const list = [...templates].sort((x, y) => (y.code === current) - (x.code === current));
  const name = (t) => (t.code === "default" ? "Clinical note" : t.name);
  const chosen = list.find((t) => t.code === selected) || list[0];
  const switching = chosen.code !== current;
  return (
    <Portal>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" role="dialog" aria-modal="true" aria-labelledby="note-format-title"
        onKeyDown={(e) => e.key === "Escape" && onLater()}>
        <div className="flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
          <div className="flex items-start gap-3 border-b border-primary-100 bg-gradient-to-r from-primary-50 to-primary-100/60 px-5 py-4">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white text-primary-600 shadow-sm ring-1 ring-primary-100">
              <FileText className="h-5 w-5" />
            </span>
            <div className="min-w-0 flex-1">
              <h2 id="note-format-title" className="text-base font-bold text-secondary-900">Confirm the note format</h2>
              <p className="text-xs text-secondary-600">Needed before completing, as your clinic has no default yet</p>
            </div>
            <button type="button" onClick={onLater} aria-label="Back to the note" className="-mr-1 -mt-1 rounded-full p-1.5 text-secondary-600 hover:bg-white/70">
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="flex-1 space-y-2 overflow-y-auto px-5 py-4" role="radiogroup" aria-labelledby="note-format-title">
            {list.map((t) => {
              const on = t.code === selected;
              const sections = t.code === "default" ? DEFAULT_SECTIONS : (t.sections || []).map((x) => x.label);
              return (
                <button key={t.code} type="button" role="radio" aria-checked={on} onClick={() => setSelected(t.code)}
                  className={`flex w-full items-start gap-3 rounded-xl border px-3.5 py-3 text-left transition-colors ${on ? "border-primary-500 bg-primary-50/60 ring-1 ring-primary-500" : "border-secondary-200 hover:bg-secondary-50"}`}>
                  <span className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 ${on ? "border-primary-600" : "border-secondary-300"}`}>
                    {on && <span className="h-2 w-2 rounded-full bg-primary-600" />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center gap-1.5">
                      <span className="text-sm font-semibold text-secondary-900">{name(t)}</span>
                      {t.code === current && <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10.5px] font-semibold text-emerald-800">Written in</span>}
                      {t.source === "org" && <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-[10.5px] font-semibold text-secondary-600">Your clinic</span>}
                    </span>
                    <span className="mt-1 flex flex-wrap gap-1">
                      {sections.slice(0, 6).map((label) => (
                        <span key={label} className="max-w-[12rem] truncate rounded-md bg-white px-1.5 py-0.5 text-[11px] text-secondary-600 ring-1 ring-secondary-200">{label}</span>
                      ))}
                      {sections.length > 6 && <span className="text-[11px] text-secondary-500">+{sections.length - 6} more</span>}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>

          <div className="space-y-2 border-t border-secondary-100 bg-secondary-50/60 px-5 py-3">
            {switching && (
              <p className="text-xs text-amber-800">The note will switch to {name(chosen)} for you to write in. What you wrote in {name(list[0])} won't show there.</p>
            )}
            <p className="text-[11px] text-secondary-500">Your admin can set a default in Settings, Clinical & Safety.</p>
            <div className="flex justify-end">
              <div className="flex gap-2">
                <button type="button" onClick={onLater}
                  className="rounded-lg border border-secondary-300 bg-white px-3.5 py-2 text-sm font-semibold text-secondary-700 hover:bg-secondary-50">Cancel</button>
                <button type="button" onClick={() => onPick(chosen.code)}
                  className="rounded-lg bg-primary-600 px-3.5 py-2 text-sm font-bold text-white hover:bg-primary-700">
                  {switching ? `Switch to ${name(chosen)}` : "Complete"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
}

// The clinician's pick on the page counts as this session's choice too, so
// the drafts follow it even before the note is first saved.
export function rememberSessionNoteFormat(appointmentId, code) {
  if (appointmentId) api.setNoteFormat(appointmentId, code || "default").catch(() => {});
}
